// 3rd party libs
import React, { useEffect, useState, useCallback } from 'react';
import { VictoryAxisProps } from 'victory';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

// Components
import { BarChart, DashboardWidget, useDateRange } from 'components';

// Types
import { ProteinMachineRouteQueryParams, MachineUtilization } from 'types/protein';

// Api
import { useGetMachineUtilizationQuery } from 'api';

// Theme
//import theme from 'themes';
import victoryTheme from 'themes/victory';
import { DATA_DATE_LIMIT_DAYS } from 'constants/machineConfig';
import styled from 'styled-components';
import { machineUtilizationColors } from 'common/pages/fleetV2/settings/protein/colors';
import styledTheme from 'common/theme';

const InitYAxisStyle: VictoryAxisProps = {
  style: {
    tickLabels: {
      padding: 2,
      fill: styledTheme.colors.gray700,
      fontSize: 12,
      lineHeight: 12,
      fontWeight: 400
    }
  }
};

const InitXAxisStyle: VictoryAxisProps = {
  style: {
    tickLabels: {
      padding: 10,
      fill: styledTheme.colors.gray700,
      fontSize: 14,
      lineHeight: 12,
      fontWeight: 600
    }
  }
};

const ChartStyle = styled.div`
  overflow: auto;
`;

const getDurationInMs = (value) => {
  value = Number(value)
  const durationValue =  value ? (value / (1000 * 60 * 60)).toFixed(2) : 0;
  return Number(durationValue);
}

const toBarChartInput = (t: TFunction<'mh'[], undefined>, data?: MachineUtilization) => {
  if (!data) return [];
  const { run, clean, idle, paused, stopByAlarm } = machineUtilizationColors;
  return [
    { x: t('running'), y: getDurationInMs(data?.runningDuration), color: run },
    { x: t('cleaning'), y: getDurationInMs(data?.cleaningDuration), color: clean },
    { x: t('idle'), y: getDurationInMs(data?.idleDuration), color: idle },
    { x: t('paused'), y: getDurationInMs(data?.stoppedDuration), color: paused },
    { x: t('stop_by_alarm'), y: getDurationInMs(data?.stopAlarmDuration), color: stopByAlarm }
  ];
};

const getBarWidth = (chartWidth: number, data?: MachineUtilization) => {
  if (!data) return 0;
  return chartWidth / Object.keys(data).length;
};

interface ChartProps {
  width: number;
  height: number;
  data?: MachineUtilization;
}
const MachineUtilizationChart = ({ width, height, data }: ChartProps): JSX.Element => {
  const [xAxis, setXAxis] = useState<VictoryAxisProps>(InitXAxisStyle);
  const [yAxis, setYAxis] = useState<VictoryAxisProps>(InitYAxisStyle);
  const { t } = useTranslation(['mh']);
  useEffect(() => {
    // Make the labels tilt when the width becomes too small
    if (width < 350) {
      const nextXAxis: VictoryAxisProps = { ...xAxis };
      nextXAxis.style = {
        ...nextXAxis.style,
        tickLabels: {
          ...xAxis.style?.tickLabels,
          angle: -35,
          fontSize: 10,
          textAnchor: 'end'
        }
      };
      setXAxis(nextXAxis);
    } else setXAxis(InitXAxisStyle);

    if (width < 250) {
      const nextYAxis: VictoryAxisProps = { ...yAxis };
      nextYAxis.style = {
        ...nextYAxis.style,
        tickLabels: {
          ...yAxis.style?.tickLabels,
          angle: 35,
          fontSize: 11,
          textAnchor: 'start'
        }
      };
      setYAxis(nextYAxis);
    } else setYAxis(InitYAxisStyle);
  }, [width]);

  return (
    <ChartStyle>
      <BarChart
        domainPadding={{
          x: getBarWidth(width, data) / 2.5
        }}
        theme={victoryTheme}
        dims={{ width, height }}
        bars={toBarChartInput(t, data)}
        format={(tick) => `${tick}%`}
        config={{
          bar: {
            barRatio: 1,
            labels: ({ datum }) => `${datum.y} Hrs`,
            style: { labels: { fontSize: 16, fontWeight: 600, fill: styledTheme.colors.gray700 } }
          },
          xAxis,
          yAxis
        }}
        padding={{ bottom: width < 350 ? 45 : 40 }}
      />
    </ChartStyle>
  );
};

const MachineUtilzation = (): JSX.Element => {
  const { dateRange } = useDateRange();
  const { t } = useTranslation(['mh']);

  const startDatetime = dateRange.startTime.toISOString();
  const endDatetime = dateRange.endTime.toISOString();

  const { machineId } = useParams<ProteinMachineRouteQueryParams>();

  const { data, isLoading, error } = useGetMachineUtilizationQuery({
    machineId,
    startDatetime,
    endDatetime
  });

  const widgetSettings = {
    isLoading,
    title: t('machine_utilization') as string,
    showDateRange: true,
    hasDatePicker: true,
    hasGoBackDateLimit: DATA_DATE_LIMIT_DAYS,
    hasError: error && t('failed_to_load_machine_utilization'),
    dateRange
  };

  const Chart = useCallback(
    ({ width, height }: ChartProps): JSX.Element => {
      return <MachineUtilizationChart {...{ width, height, data }} />;
    },
    [data]
  );

  return <DashboardWidget {...widgetSettings} Load={Chart} />;
};
export default MachineUtilzation;
